import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Upload, FileText, Image as ImageIcon, Loader2, X, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIBookAssistant from '../../components/AIBookAssistant';

const SubmitBook = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    boek_naam: '',
    skrywer_naam: '',
    genre: [],  // Array for multiple genres
    ouderdomsgroep: '',
    blurb: '',
  });
  const [bookFile, setBookFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [coverMethod, setCoverMethod] = useState('upload'); // 'upload' or 'ai'
  const [blurbMethod, setBlurbMethod] = useState('manual'); // 'manual' or 'ai'
  const [availableGenres, setAvailableGenres] = useState([]);
  const [newGenre, setNewGenre] = useState('');
  const [extractedPreview, setExtractedPreview] = useState('');
  const [extractingPreview, setExtractingPreview] = useState(false);

  const ageGroups = ['0-5 jaar', '6-9 jaar', '10-12 jaar', '13-17 jaar', '18+ jaar'];

  useEffect(() => {
    fetchGenres();
  }, []);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      // API returns array directly
      setAvailableGenres(response.data || []);
    } catch (error) {
      console.error('Failed to fetch genres:', error);
      // Fallback genres
      setAvailableGenres(['Fiksie', 'Nie-fiksie', 'Romanties', 'Spanningsroman', 'Fantasie']);
    }
  };

  const toggleGenre = (genre) => {
    if (formData.genre.includes(genre)) {
      setFormData({
        ...formData,
        genre: formData.genre.filter(g => g !== genre)
      });
    } else {
      setFormData({
        ...formData,
        genre: [...formData.genre, genre]
      });
    }
  };

  const addNewGenre = () => {
    if (newGenre.trim() && !availableGenres.includes(newGenre.trim())) {
      const trimmedGenre = newGenre.trim();
      setAvailableGenres([...availableGenres, trimmedGenre].sort());
      setFormData({
        ...formData,
        genre: [...formData.genre, trimmedGenre]
      });
      setNewGenre('');
      toast.success(`Genre "${trimmedGenre}" bygevoeg!`);
    }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image/(jpeg|jpg|png)')) {
        toast.error('Voorblad moet .JPG of .PNG formaat wees');
        return;
      }
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleBookFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (ext !== 'pdf' && ext !== 'docx' && ext !== 'epub') {
        toast.error('Boek moet .PDF, .DOCX of .EPUB formaat wees');
        return;
      }
      setBookFile(file);
      
      // Extract preview text for AI features
      if (blurbMethod === 'ai' || coverMethod === 'ai') {
        await extractPreviewFromFile(file);
      }
    }
  };

  const extractPreviewFromFile = async (file) => {
    setExtractingPreview(true);
    try {
      const formData = new FormData();
      formData.append('book_file', file);
      
      const response = await axios.post(`${API}/extract-book-preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExtractedPreview(response.data.preview_text);
      toast.success('Boek voorskou onttrek vir AI gebruik');
    } catch (error) {
      console.error('Preview extraction error:', error);
      toast.error('Kon nie boek voorskou onttrek nie. AI funksies sal beperk wees.');
    } finally {
      setExtractingPreview(false);
    }
  };

  const handleBlurbGenerated = (blurbText) => {
    setFormData({ ...formData, blurb: blurbText });
  };

  const handleCoverSelect = (file, preview) => {
    setCoverImage(file);
    setCoverPreview(preview);
  };

  // Auto-extract preview when switching to AI mode
  useEffect(() => {
    if ((blurbMethod === 'ai' || coverMethod === 'ai') && bookFile && !extractedPreview && !extractingPreview) {
      extractPreviewFromFile(bookFile);
    }
  }, [blurbMethod, coverMethod, bookFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookFile || !coverImage) {
      toast.error('Beide boek file en voorblad is vereis');
      return;
    }

    if (formData.genre.length === 0) {
      toast.error('Kies ten minste een genre');
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append('boek_naam', formData.boek_naam);
    data.append('skrywer_naam', formData.skrywer_naam);
    data.append('genre', JSON.stringify(formData.genre)); // Send as JSON array
    data.append('ouderdomsgroep', formData.ouderdomsgroep);
    data.append('blurb', formData.blurb);
    data.append('book_file', bookFile);
    data.append('cover_image', coverImage);

    try {
      const response = await axios.post(`${API}/books/submit`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data?.mode === 'correction') {
        toast.success(
          response.data.message ||
          'Hierdie boek het gewag op ’n herlaai. Jou nuwe lêer is gestoor as ’n regstelling en wag op goedkeuring.',
          { duration: 6000 }
        );
      } else {
        toast.success('Boek suksesvol ingedien!');
      }
      navigate('/writer/submissions');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Indiening het misluk');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Dien Boek In
        </h2>
        <p className="text-gray-600">Voltooi die vorm om jou boek in te dien</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Boek Inligting</CardTitle>
          <CardDescription>Verskaf al die nodige inligting oor jou boek</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="boek_naam">Boek Naam *</Label>
                <Input
                  id="boek_naam"
                  data-testid="book-name-input"
                  required
                  value={formData.boek_naam}
                  onChange={(e) => setFormData({ ...formData, boek_naam: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="skrywer_naam">Skrywer Naam *</Label>
                <Input
                  id="skrywer_naam"
                  data-testid="author-name-input"
                  required
                  value={formData.skrywer_naam}
                  onChange={(e) => setFormData({ ...formData, skrywer_naam: e.target.value })}
                />
              </div>
            </div>

            {/* Multi-Genre Selection */}
            <div className="space-y-2">
              <Label>Genre(s) * (Kies een of meer)</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {availableGenres.map((genre) => (
                  <Button
                    key={genre}
                    type="button"
                    variant={formData.genre.includes(genre) ? 'default' : 'outline'}
                    className={`text-sm ${
                      formData.genre.includes(genre)
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'hover:bg-orange-50'
                    }`}
                    onClick={() => toggleGenre(genre)}
                  >
                    {genre}
                  </Button>
                ))}
              </div>

              {/* Add New Genre */}
              <div className="flex gap-2 mt-3">
                <Input
                  placeholder="Voeg nuwe genre by..."
                  value={newGenre}
                  onChange={(e) => setNewGenre(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addNewGenre();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={addNewGenre}
                  disabled={!newGenre.trim()}
                >
                  Voeg By
                </Button>
              </div>

              {/* Selected Genres Display */}
              {formData.genre.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className="text-sm text-gray-600">Gekies:</span>
                  {formData.genre.map((genre) => (
                    <span
                      key={genre}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800"
                    >
                      {genre}
                      <button
                        type="button"
                        onClick={() => toggleGenre(genre)}
                        className="ml-2 hover:text-orange-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="ouderdomsgroep">Ouderdomsgroep *</Label>
              <select
                id="ouderdomsgroep"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={formData.ouderdomsgroep}
                onChange={(e) => setFormData({ ...formData, ouderdomsgroep: e.target.value })}
                required
              >
                <option value="">Kies ouderdomsgroep</option>
                {ageGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </div>

            {/* Blurb Method Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Blurb (Agterblad Beskrywing) *</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={blurbMethod === 'manual' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBlurbMethod('manual')}
                    className={blurbMethod === 'manual' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    Self Skryf
                  </Button>
                  <Button
                    type="button"
                    variant={blurbMethod === 'ai' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBlurbMethod('ai')}
                    className={blurbMethod === 'ai' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    AI Genereer
                  </Button>
                </div>
              </div>

              {blurbMethod === 'manual' ? (
                <Textarea
                  id="blurb"
                  data-testid="blurb-input"
                  required
                  rows={5}
                  placeholder="Beskryf jou boek in 100-500 karakters..."
                  value={formData.blurb}
                  onChange={(e) => setFormData({ ...formData, blurb: e.target.value })}
                />
              ) : (
                <div className="space-y-4">
                  {!bookFile ? (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <p className="text-sm text-orange-800">
                        <FileText className="inline h-4 w-4 mr-1" />
                        Laai eers jou boek file op om AI blurb en voorblaaie te genereer
                      </p>
                    </div>
                  ) : extractingPreview ? (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <Loader2 className="inline h-5 w-5 animate-spin text-blue-600 mr-2" />
                      <span className="text-sm text-blue-800">Besig om boek te analiseer vir AI gebruik...</span>
                    </div>
                  ) : (
                    <AIBookAssistant
                      boekNaam={formData.boek_naam}
                      skrywerNaam={formData.skrywer_naam}
                      genre={formData.genre.join(', ')}
                      previewText={extractedPreview}
                      bookFile={bookFile}
                      onBlurbGenerated={handleBlurbGenerated}
                      onCoverSelect={handleCoverSelect}
                      initialBlurb={formData.blurb}
                    />
                  )}
                </div>
              )}
              {formData.blurb && (
                <p className="text-sm text-gray-500">{formData.blurb.length} karakters</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="book_file">Boek File (.PDF, .DOCX of .EPUB) *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-400 transition">
                  <input
                    id="book_file"
                    data-testid="book-file-input"
                    type="file"
                    accept=".pdf,.docx,.epub,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/epub+zip"
                    onChange={handleBookFileChange}
                    className="hidden"
                  />
                  <label htmlFor="book_file" className="cursor-pointer flex flex-col items-center">
                    <FileText className="h-12 w-12 text-gray-400 mb-2" />
                    {bookFile ? (
                      <span className="text-sm text-gray-900 font-semibold">{bookFile.name}</span>
                    ) : (
                      <>
                        <span className="text-sm text-gray-600">Klik om boek op te laai</span>
                        <span className="text-xs text-gray-400">PDF, DOCX of EPUB (Maks 50MB)</span>
                      </>
                    )}
                  </label>
                </div>
              </div>

              <div className="space-y-4">
                <Label>Voorblad Metode *</Label>
                <RadioGroup value={coverMethod} onValueChange={setCoverMethod}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="upload" id="upload" />
                    <Label htmlFor="upload" className="cursor-pointer">Laai eie voorblad op</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="ai" id="ai" />
                    <Label htmlFor="ai" className="cursor-pointer">
                      <Sparkles className="inline h-4 w-4 mr-1" />
                      Genereer AI voorblad
                    </Label>
                  </div>
                </RadioGroup>

                {coverMethod === 'upload' ? (
                  <div className="space-y-2">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 hover:border-orange-400 transition">
                      <input
                        id="cover_image"
                        data-testid="cover-image-input"
                        type="file"
                        accept=".jpg,.jpeg,.png"
                        onChange={handleCoverChange}
                        className="hidden"
                      />
                      <label htmlFor="cover_image" className="cursor-pointer flex flex-col items-center">
                        {coverPreview ? (
                          <img src={coverPreview} alt="Cover preview" className="h-32 w-auto object-contain mb-2 rounded" />
                        ) : (
                          <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
                        )}
                        <span className="text-sm text-gray-600">Klik om voorblad op te laai</span>
                        <span className="text-xs text-gray-400">JPG of PNG - Aanbeveel 1024×1536</span>
                      </label>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {!coverPreview ? (
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <p className="text-sm text-gray-700 mb-2">
                          <Sparkles className="inline h-4 w-4 mr-1 text-orange-600" />
                          Gebruik die AI Blurb Generator hierbo om voorblaaie te genereer
                        </p>
                        <ol className="text-xs text-gray-600 ml-4 list-decimal space-y-1">
                          <li>Laai jou boek file op</li>
                          <li>Klik op "AI Genereer" by Blurb</li>
                          <li>Klik "Genereer Voorblaaie" in die AI assistent</li>
                        </ol>
                      </div>
                    ) : (
                      <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                        <img src={coverPreview} alt="Selected AI cover" className="h-48 w-auto mx-auto object-contain rounded shadow-lg mb-2" />
                        <p className="text-sm text-green-600 font-medium">✓ AI voorblad gekies</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Button
              data-testid="submit-book-btn"
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 py-6 text-lg"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Besig om in te dien...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-5 w-5" />
                  Dien Boek In
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubmitBook;
